import React from 'react';
import ChonkyFileBrowser, {
	renderFolderIcon,
} from './Chonky/ChonkyFileBrowser';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import {
	ChonkyActions, ChonkyIconName,
} from 'chonky';
import {
	handlePendingChangesNavigation,
} from 'Vulture/lib/Nav';
import {
	isAdministrativeUser,
} from 'Vulture/Session';
import {
	shouldSkipSlide,
} from './utils/slides';
import Img from 'wrapper/Img';

const formatDateTime = (d) => {
	if (!d) return '-';
	try {
		const dateObj = new Date(d);
		if (isNaN(dateObj.getTime())) return '-';

		// Format date as MM/DD/YYYY
		const month = String(dateObj.getMonth() + 1).padStart(2, '0');
		const day = String(dateObj.getDate()).padStart(2, '0');
		const year = dateObj.getFullYear();
		const dateStr = `${month}/${day}/${year}`;

		// Format time as H:MM AM/PM
		let hours = dateObj.getHours();
		const minutes = String(dateObj.getMinutes()).padStart(2, '0');
		const ampm = hours >= 12 ? 'PM' : 'AM';
		hours = hours % 12;
		hours = hours ? hours : 12; // 0 should be 12
		const timeStr = `${hours}:${minutes} ${ampm}`;

		return (
			<div style={{ lineHeight: '1.4' }}>
				<div>{dateStr}</div>
				<div>{timeStr}</div>
			</div>
		);
	} catch (e) {
		return '-';
	}
};

// Module-scope so table re-renders don't remount it (which reset `src` to the
// favicon and re-issued the 404 before falling back — flicker + repeat requests).
const Thumbnail = ({
	file,
}) => {
	const [ errored, setErrored, ] = React.useState(false);
	// URL slides use the site favicon as thumbnail; many sites have none,
	// so fall back to the generic url icon instead of a broken image.
	const [ src, setSrc, ] = React.useState(file.thumbnailUrl);

	// Rows are keyed by slide id, so an edited URL (new favicon) updates the same
	// mounted instance — re-seed instead of showing the previous site's icon.
	React.useEffect(() => {
		setSrc(file.thumbnailUrl);
		setErrored(false);
	}, [ file.thumbnailUrl, file.fallbackThumbnailUrl, ]);

	return (
		<div
			style={{
				display:        'flex',
				justifyContent: 'center',
				width:          '100%',
			}}
		>
			<Img
				src={src}
				alt={file.name}
				title={file.name}
				onError={() => {
					if (file.fallbackThumbnailUrl && src !== file.fallbackThumbnailUrl) {
						setSrc(file.fallbackThumbnailUrl);
						return;
					}
					setErrored(true);
				}}
				style={{
					maxWidth:     errored ? '100%' : '70px',
					maxHeight:    '50px',
					objectFit:    'cover',
					borderRadius: 4,
					textOverflow: 'ellipsis',
					whiteSpace:   'nowrap',
				}}
			/>
		</div>
	);
};

class SlideDeckSlidesTableView extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			slideDeckName: props.slideDeck.name || 'Slide Deck Name',
		};
	}

	componentDidMount() {
		this.props.loadSlides(this.props?.slideDeck?.id || null);
	}

	render() {
		const {
			slides,
			slideDeck,
			contentManagerOptions,
			originalContentManagers,
			isInFolderSchedulingEnabled,
		} = this.props;

		const {
			slideDeckName,
		} = this.state;

		const chonkyFiles = slides.map((s) => {
			const uuid = s.slideDeckSlideID ? `sds-${s.slideDeckSlideID}` : (s.slideID ? `s-${s.slideID}` : `f-${s.file_name}`);
			let scheduling;
			if (s.scheduling) {
				try {
					scheduling = JSON.parse(JSON.stringify(s.scheduling));
				} catch (e) {
					scheduling = {
						...s.scheduling,
					};
				}
			}
			const start_date = s.start_date || s.startDate || scheduling?.start_date || null;
			const end_date = s.end_date || s.endDate || scheduling?.end_date || null;

			// Use shouldSkipSlide to determine if slide is currently inactive
			const isInactive = s.scheduled === true && shouldSkipSlide({
				...s,
				scheduling,
			});

			return {
				id:           uuid,
				name:         s.description,
				isDir:        false,
				order:        s.order,
				duration:     s.duration,
				resolution:   s.resolution,
				thumbnailUrl: s.src,
				fallbackThumbnailUrl: s.fallbackSrc,
				folderSlots:  s.folderSlots,
				start_date,
				end_date,
				isInactive,
				raw:          {
					...s,
					scheduling,
				},
			};
		});

		const chonkyFileMap = {
			'slide-decks': {
				id:          'slide-decks',
				name:        'Slide Decks',
				isDir:       true,
				childrenIds: [ 'slides', ],
				parentId:    null,
			},
			slides: {
				id:          'slides',
				name:        slideDeckName || 'Slides',
				isDir:       true,
				childrenIds: chonkyFiles.map((f) => {
					return f.id;
				}),
				parentId: 'slide-decks',
			},
			...Object.fromEntries(chonkyFiles.map((f) => {
				return [
					f.id, {
						...f,
						parentId: 'slides',
					},
				];
			})),
		};

		const injectedColumns = [
			{
				id:       'drag_handle',
				label:    '',
				centerColumn: true,
				accessor: () => {
					return (
						<span style={{
							display:        'inline-flex',
							alignItems:     'center',
							justifyContent: 'center',
							width:          18,
						}}>
							<CustomIcon icon="grip-vertical" style={{
								cursor:  'grab',
								opacity: 0.6,
							}} />
						</span>
					);
				},
				width:    '50px',
				notSortable: true,
			},
			{ id: 'order', label: 'Order/Priority', centerColumn: true, accessor: (f) => f.order },
			{ id: 'image', notSortable: true, label: (<div style={{ textAlign: 'center', width: '100%', paddingRight: '15px' }}>Image</div>), accessor: (f) => {
				if (f.raw.kind === 'folder') {
					return (
						<div style={{ display: 'flex', justifyContent: 'center' }}>
							{renderFolderIcon(50)}
						</div>
					);
				}

				return <Thumbnail file={f} />;
			} },
			{ id: 'name', label: 'Description', accessor: (f) => f.name },
			{ id: 'slots', label: '# of Slots', centerColumn: true, accessor: (f) => f.folderSlots || 1 },
			{ id: 'duration', label: 'Duration (Per Slot)', centerColumn: true, accessor: (f) => Math.round(f.duration) },
			{ id: 'resolution', label: 'Resolution', centerColumn: true, accessor: (f) => f.resolution || '-' },
			{ id: 'start_date', label: 'Starting', centerColumn: true, accessor: (f) => formatDateTime(f.start_date) },
			{ id: 'end_date', label: 'Ending', centerColumn: true, accessor: (f) => formatDateTime(f.end_date) },
		];

		const injectedActions = [
			{
				id:      'custom_injected_new_slide',
				name:    'New Slide',
				icon:    ChonkyIconName.file,
				handler: () => {
					this.props.addSlideModal();
				},
			},
			{
				id:      'delete_selected_slides',
				name:    'Delete Selected',
				icon:    ChonkyIconName.trash,
				handler: ({
					selection,
				}) => {
					if (!selection || selection.length === 0) return;
					if (this.props.deleteSelectedSlides) {
						const selectedIds = selection.map((s) => {
							return typeof s === 'string' ? s : s?.id;
						}).filter(Boolean);
						this.props.deleteSelectedSlides(selectedIds);
					}
				},
			},
		];

		const handleBreadcrumbClick = () => {
			const {
				app,
			} = this.props;

			handlePendingChangesNavigation(app, this.props.clearSlideDeck);
			// Refresh slide deck data when returning to SlideDeckTable
			if (typeof this.props.loadSlideDecks === 'function') {
				this.props.loadSlideDecks();
			}
		};

		const initialSelectedContentManagers = originalContentManagers
			.filter((cm) => {
				return cm.slide_deck_id === slideDeck.id;
			})
			.map((cm) => {
				return contentManagerOptions.find((option) => {
					return option.value === cm.user.uuid;
				});
			})
			.filter(Boolean); // Remove undefined values if user not found in options

		return (
			<>
				<div style={{
					padding: '8px',
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
				}}>
					<ChonkyFileBrowser
						mode="slideDeckSlides"
						app={this.props.app}
						readOnly={false}
						addSelectAllToggleAction={true}
						accountUUID={this.props.accountUUID}
						injectedFileMap={chonkyFileMap}
						injectedColumns={injectedColumns}
						{...(isAdministrativeUser() && {
							injectedHeaderDropdown:             contentManagerOptions,
							injectedHeaderDropdownInitialValue: initialSelectedContentManagers,
						})}
						defaultSortColumn="order"
						defaultSortDirection="asc"
						injectedActions={injectedActions}
						onBreadcrumbClick={handleBreadcrumbClick}
						searchAllColumns={true}
						initialFolderId="slides"
						onInjectedFileOpen={(file) => {
							if (file?.raw) {
								let cloned = JSON.parse(JSON.stringify(file.raw));
								this.props.editSlideModal(cloned);
							}
						}}
						onInjectedEdit={(file) => {
							if (file?.raw) {
								let cloned = JSON.parse(JSON.stringify(file.raw));
								this.props.editSlideModal(cloned);
							}
						}}
						onInjectedDelete={(file) => {
							if (file?.raw) {
								this.props.deleteSingleSlide(file.raw, file.raw.slideDeckSlideID);
							}
						}}
						{...(isInFolderSchedulingEnabled && {
							onInjectedCalendar: (file) => {
								if (file?.raw && this.props.openFolderScheduling) {
									this.props.openFolderScheduling(file.raw);
								}
							},
						})}
						onInjectedReorderProgress={(_, oldIndex, newIndex) => {
							this.props.onReorder(oldIndex, newIndex);
						}}
						onInjectedHeaderDropdownChange={(options) => {
							this.props.onSelectContentManagers(options);
						}}
						injectedColumnDefaults={{
							image: '200px',
							name: '280px',
							drag_handle: '50px',
							order: '66px',
							slots: '105px',
							duration: '160px',
							start_date: '100px',
							end_date: '95px',
							resolution: '95px',
						}}
						defaultFileViewActionId={ChonkyActions.EnableListView.id}
						onInjectedCreate={this.props.addSlideModal}
						hideListViewOptions={true}
					/>
				</div>
			</>
		);
	}
}

export default SlideDeckSlidesTableView;
